import EditIcon from '@mui/icons-material/Edit';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { postEmailRecipientForm } from '../../../../api/emailRecipients';
import { getRecipientTypes } from '../../../../redux/slices/emailRecipient';
import { useDispatch, useSelector } from '../../../../redux/store';
import { FORGOT_PASSWORD_SCHEMA } from '../../../../reusable-components/form/authSchema';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

EmailRecipientModal.propTypes = {
  edit: PropTypes.bool,
  row: PropTypes.object,
  fetchData: PropTypes.func,
};

export default function EmailRecipientModal({ edit, row, fetchData }) {
  const {
    data: { sites, recipientTypes, notificationTypes },
  } = useSelector((state) => state.emailRecipient);
  const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [emailError, setEmailError] = useState('');
  const [value, setValue] = useState({
    email: null,
    id: null,
    notificationType: null,
    recipientType: null,
    siteId: null,
  });

  useEffect(() => {
    if (value.siteId > 0) dispatch(getRecipientTypes(value.siteId));
  }, [dispatch, value.siteId, open]);

  const validateEmail = async () => {
    try {
      await FORGOT_PASSWORD_SCHEMA.validateAt('email', { email: value.email });
      setEmailError('');
      return true;
    } catch (error) {
      setEmailError(error.message);
      return false;
    }
  }; 

  const recipientTypeValue = useMemo(() => recipientTypes?.find((s) => s?.value === value.recipientType) ?? null, [recipientTypes, value.recipientType]);
  const notificationTypeValue = useMemo(() => notificationTypes.find((r) => r?.value === value.notificationType) ?? null, [notificationTypes, value.notificationType]);

  const handleSave = async () => {
    const isValidEmail = await validateEmail();
    if (!isValidEmail) {
      setEmailError('Please write a correct email address');
      return;
    }
    const response = await postEmailRecipientForm(value, edit ? row?.id : null);
    if (response) {
      await fetchData();
      setOpen(false);
      enqueueSnackbar('Email updated successfully', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to updated email', { variant: 'error' });
    }
  };

  const handleClickOpen = () => {
    setValue({
      email: edit ? row.email : null,
      id: edit ? row.id : null,
      notificationType: edit ? row.notificationType : null,
      recipientType: edit ? row.recipientType : null,
      siteId: edit ? row.site?.value : user.siteId,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setValue({
      email: null,
      id: null,
      notificationType: null,
      recipientType: null,
      siteId: user?.siteId ?? null,
    });
    setOpen(false);
  };

  return (
    <>
      {edit ? (
        <Tooltip title="Edit">
          <IconButton edge="end" color="secondary" onClick={handleClickOpen} sx={{ mr: 0.5 }}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="contained"
          color="secondary"
          sx={{ mr: 1 }}
          size="small"
          onClick={handleClickOpen}
          startIcon={<Iconify icon="eva:plus-outline" />}
        >
          Add Recipient
        </Button>
      )}

      <Dialog open={open} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>
          {edit ? 'Edit' : 'Add'} Email Recipient
         <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent sx={{ height: '100%', width: '100%' }}>
          <Grid container direction="row" justifyContent="center" alignItems="flex-start" sx={{ p: 1 }}>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1">Email</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={value.email ?? ""}
                  onChange={(e) =>
                    setValue({
                      ...value,
                      email: e.target.value,
                    })
                  }
                  label=""
                  sx={{
                    mt: 1,
                    '& legend': { display: 'none' },
                    '& fieldset': { top: 0 },
                    '& .MuiFormLabel-root ': { display: 'none' },
                  }}
                  error={!!emailError}
                  helperText={emailError}
                  autoComplete="off"
                />
              </Box>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1">Site</Typography>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={sites.find((s) => s?.value === value.siteId)}
                  onChange={(event, newValue) => {
                    setValue({ ...value, siteId: newValue?.value });
                  }}
                  options={sites}
                  renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                  sx={{
                    mt: 1,
                    '& legend': { display: 'none' },
                    '& fieldset': { top: 0 },
                    '& .MuiFormLabel-root ': { display: 'none' },
                  }}
                />
              </Box>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1">Send For</Typography>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={notificationTypeValue}
                  onChange={(event, newValue) => {
                    setValue({
                      ...value,
                      notificationType: newValue?.value,
                    });
                  }}
                  options={notificationTypes}
                  renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                  sx={{
                    mt: 1,
                    '& legend': { display: 'none' },
                    '& fieldset': { top: 0 },
                    '& .MuiFormLabel-root ': { display: 'none' },
                  }}
                />
              </Box>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              sx={{ mt: 1.5 }}
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1">Send Type</Typography>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={recipientTypeValue}
                  onChange={(event, newValue) => {
                    setValue({
                      ...value,
                      recipientType: newValue?.value ?? null,
                    });
                  }}
                  options={recipientTypes}
                  renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                  sx={{
                    mt: 1,
                    '& legend': { display: 'none' },
                    '& fieldset': { top: 0 },
                    '& .MuiFormLabel-root ': { display: 'none' },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            sx={{ width: 150 }}
            onClick={handleSave}
            disabled={!value.email || !value.notificationType || !value.recipientType || !value.siteId}
          >
            {edit ? 'Save' : 'Add Recipient'}
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
